import type { RouterOutputs } from "@apps/server/trpc/routers";
import { ActionIcon, Anchor, Button, Flex, Group, Modal, Select, Text, Title, Tooltip } from "@mantine/core";
import type { FieldIndentifier } from "@packages/lib";
import { IconFilter, IconPhone, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  MRT_GlobalFilterTextInput,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  useMantineReactTable,
} from "mantine-react-table";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useCallback, useMemo } from "react";
import { useConfigStore } from "~/dexie/store";
import { getConfigByField } from "~/dexie/sync";
import { useBoolean } from "~/hooks/use-boolean";
import { errorNotification, successNotification } from "~/lib/notifications";
import { trpc } from "~/lib/trpc";
import { DealStatusBadge } from "./$dealId";

type Deal = RouterOutputs["deal"]["getDeals"][number];

const dealFilters = [
  { value: "all", label: "All" },
  { value: "agreements", label: "Signed" },
  { value: "offers", label: "Quotes" },
  { value: "partially_signed", label: "Partially Signed" },
  { value: "leads", label: "Leads" },
] as const satisfies ReadonlyArray<{ label: string; value: string }>;

type DealFilterType = (typeof dealFilters)[number]["value"];

export const Route = createFileRoute("/_authenticated/deals/")({
  component: RouteComponent,
  loader: async ({ context: { queryClient, trpc } }) => {
    const deals = await queryClient.ensureQueryData(trpc.deal.getDeals.queryOptions());
    return { deals };
  },
});

function RouteComponent() {
  const [filter, setFilter] = useQueryState(
    "filter",
    parseAsStringLiteral(dealFilters.map((f) => f.value)).withDefault("all"),
  );
  const navigate = useNavigate();
  const loaderData = Route.useLoaderData();
  const deleteModal = useBoolean();
  const deals = useQuery({
    ...trpc.deal.getDeals.queryOptions(),
    initialData: loaderData.deals,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: 10_000,
  });
  const qc = useQueryClient();

  const deleteDeals = useMutation(
    trpc.deal.delete.mutationOptions({
      onSuccess: (_, vars) => {
        table.resetRowSelection();
        qc.invalidateQueries(trpc.deal.getDeals.queryOptions());
        successNotification(`Deleted ${vars.ids.length} deals`);
        deleteModal.close();
      },
      onError: (error) => {
        errorNotification(error.message);
      },
    }),
  );

  const data = useMemo(() => {
    return deals.data?.filter((deal) => {
      if (filter === "agreements") return deal.status === "agreement";
      if (filter === "partially_signed") return deal.status === "partially_signed";
      if (filter === "offers") return deal.status === "offer";
      if (filter === "leads") return deal.status === "lead";
      return true;
    });
  }, [filter, deals.data]);

  const columns = useMemo<MRT_ColumnDef<Deal>[]>(
    () => [
      {
        accessorFn: (row) => row.customer?.name, //accessorFn used to join multiple data into a single cell
        id: "name", //id is still required when using accessorFn instead of accessorKey
        header: getConfigByField("crm.customer.name")?.label || "",
        size: 250,
        filterVariant: "autocomplete",
      },
      {
        accessorFn: (row) => row.id,
        id: "id",
        header: "Id",
        size: 250,
        // filterVariant: "autocomplete",
      },
      {
        id: "email",
        accessorFn: (row) => row.customer?.email,
        enableClickToCopy: true,
        header: getConfigByField("crm.customer.email")?.label || "",
        size: 300,
      },
      {
        id: "phone",
        accessorFn: (row) => row.customer?.phone,
        header: getConfigByField("crm.customer.phone")?.label || "",
        size: 200,
        Cell: ({ row }) => {
          const phone = row.original.customer?.phone;
          if (!phone) return null;
          return (
            <Anchor
              href={`tel:${phone}`}
              onClick={(e) => e.stopPropagation()}
              c="gray.0"
              bdrs="xl"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                textDecoration: "none",
              }}>
              <IconPhone size={13} stroke={2.5} />
              {phone}
            </Anchor>
          );
        },
      },
      {
        id: "status",
        accessorFn: (row) => row.status,
        header: getConfigByField("crm.deal.status")?.label || "",
        size: 200,
        filterVariant: "multi-select",
        Cell: ({ row }) => <DealStatusBadge status={row.original.status} size="md" />,
      },
      {
        accessorFn: (row) => {
          if (!row.serviceDate) return null;
          const sDay = new Date(row.serviceDate);
          sDay.setHours(0, 0, 0, 0);
          return sDay;
        },
        id: "date",
        header: getConfigByField("crm.deal.serviceDate")?.label || "",
        filterVariant: "date-range",
        size: 260,
        sortingFn: "datetime",
        enableColumnFilterModes: false, //keep this as only date-range filter with between inclusive filterFn
        Cell: ({ cell }) => {
          const dt = cell.getValue<Date>();
          if (!dt) return null;
          return dayjs(dt).format("YYYY-MM-DD");
        }, //render Date as a string
        // Header: ({ column }) => <em>{column.columnDef.header}</em>, //custom header markup
      },
      {
        accessorFn: (row) => {
          const dt = row.createdAt;
          return dt ? new Date(dt) : null;
        },
        id: "createdAt",
        header: "Created At",
        filterVariant: "date-range",
        sortingFn: "datetime",
        enableColumnFilterModes: false, //keep this as only date-range filter with between inclusive filterFn
        Cell: ({ cell }) => {
          const dt = cell.getValue<Date>();
          if (!dt) return null;
          return dayjs(dt).format("YYYY-MM-DD HH:mm");
        }, //render Date as a string
      },
      {
        id: "totalAmount",
        accessorFn: (row) => row.totalAmount ? Number.parseFloat(row.totalAmount) : null,
        size: 220,
        header: getConfigByField("crm.deal.totalAmount")?.label || "",
        Cell: ({ row }) => row.original.totalAmount,
        filterVariant: "range",
      },
      {
        id: "totalToPay",
        accessorFn: (row) => row.totalToPay ? Number.parseFloat(row.totalToPay) : null,
        size: 220,
        header: getConfigByField("crm.deal.totalToPay")?.label || "",
        Cell: ({ row }) => row.original.totalToPay,
        filterVariant: "range",
      },
      {
        id: "leftToPay",
        accessorFn: (row) => row.leftToPay ? Number.parseFloat(row.leftToPay) : null,
        size: 220,
        header: getConfigByField("crm.deal.leftToPay")?.label || "",
        Cell: ({ row }) => row.original.leftToPay,
        filterVariant: "range",
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data, //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableFacetedValues: true,
    enableGrouping: true,
    // enablePinning: true,
    initialState: { showColumnFilters: false, showGlobalFilter: true },
    enablePagination: false,
    // enableDensityToggle: true,
    // createDisplayMode: "modal",

    // column
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    columnFilterDisplayMode: "popover",
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableColumnVirtualization: true,

    // row
    enableRowNumbers: true,
    enableRowSelection: true,
    enableRowVirtualization: true,

    // header
    enableStickyHeader: true,

    // footer
    enableStickyFooter: true,
    enableTableFooter: true,

    mantineTableBodyRowProps: ({ row }) => ({
      onDoubleClick: () => {
        navigate({ to: "/deals/$dealId", params: { dealId: row.original.id.toString() } });
      },
      style: {
        cursor: "pointer",
      },
    }),

    mantinePaperProps: {
      // fixes double scrollbar
      mah: "100dvh",
    },
    mantineSearchTextInputProps: {
      placeholder: "Search Deals",
    },
    renderTopToolbar: ({ table }) => {
      const getSelectedIds = () => table.getSelectedRowModel().flatRows.map((row) => row.original.id);

      return (
        <>
          <Flex p="md" justify="space-between">
            <Flex gap="xs">
              <MRT_GlobalFilterTextInput table={table} />
              <Select
                variant="filled"
                leftSection={<IconFilter size={22} />}
                value={filter}
                onChange={(val) => {
                  setFilter(val as DealFilterType);
                }}
                data={dealFilters}
              />
              <MRT_ShowHideColumnsButton table={table} />
              <MRT_ToggleFiltersButton table={table} />
            </Flex>
            <Flex style={{ gap: "8px" }}>
              <Tooltip label="New Deal" position="bottom" withArrow>
                <ActionIcon color="blue" size="lg" variant="light" onClick={() => navigate({ to: "/deals/new" })}>
                  <IconPlus />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete Deal" position="bottom" withArrow>
                <ActionIcon
                  color="red"
                  size="lg"
                  variant="light"
                  disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                  onClick={deleteModal.open}>
                  <IconTrash />
                </ActionIcon>
              </Tooltip>
            </Flex>
          </Flex>
          <Modal opened={deleteModal.state} onClose={deleteModal.close} title={<Title order={5}>Delete Deals</Title>}>
            <Text>Are you sure you want to delete {getSelectedIds().length} deals?</Text>
            <Group justify="flex-end" mt="md">
              <Button variant="neutral" onClick={deleteModal.close}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deleteDeals.isPending}
                onClick={() => deleteDeals.mutate({ ids: getSelectedIds() })}>
                Delete
              </Button>
            </Group>
          </Modal>
        </>
      );
    },
  });

  return <MantineReactTable table={table} />;
}
